import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BookOpen, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/stores/authStore";
import { worklogService } from "@/services/worklogService";
import { formatDate, toLocalYMD } from "@/lib/utils";
import { WorkItemEditor } from "@/components/worklog/WorkItemEditor";

interface DailyReportRequiredPageProps {
  onSkip?: () => void;
}

/**
 * 익일 화면 차단: 마감이 지난 미등록 일일 업무 보고가 있으면 등록 전까지 다른 화면을 쓸 수 없다.
 * ProtectedRoute 가 인라인으로 렌더링(필수서류 제출 화면과 같은 방식).
 */
export default function DailyReportRequiredPage({ onSkip }: DailyReportRequiredPageProps) {
  const { logout, clearDailyReportRequired } = useAuthStore();
  const { data: status, isLoading } = useQuery({
    queryKey: ["daily-report-status"],
    queryFn: () => worklogService.getRequiredStatus(),
  });

  const missing = useMemo(() => status?.missing_dates ?? [], [status]);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (missing.length === 0) return;
    if (!selected || !missing.includes(selected)) setSelected(missing[0]);
  }, [missing, selected]);

  // 상태가 갱신되어 미등록이 모두 해소되면 차단 해제
  useEffect(() => {
    if (status && !status.block_next_day_active) {
      clearDailyReportRequired();
    }
  }, [status, clearDailyReportRequired]);

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-8">
      <div className="mx-auto w-full max-w-3xl space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-bold">일일 업무 보고 등록</h1>
          </div>
          <div className="flex items-center gap-2">
            {onSkip && (
              <Button variant="outline" size="sm" onClick={onSkip}>
                나중에 등록
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => logout()}>
              <LogOut className="h-4 w-4 mr-1" />
              로그아웃
            </Button>
          </div>
        </div>

        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              마감이 지난 미등록 보고 {missing.length}건
            </CardTitle>
            <CardDescription>
              회사 정책에 따라 일일 업무 보고는 다음 근무일 {status?.deadline_time ?? "09:00"}까지 등록해야 합니다.
              미등록 보고를 모두 등록하면 다른 화면을 이용할 수 있습니다. (지각 제출로 기록됩니다)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {missing.map((d) => (
                <Button
                  key={d}
                  size="sm"
                  variant={selected === d ? "default" : "outline"}
                  onClick={() => setSelected(d)}
                >
                  {formatDate(d)}
                </Button>
              ))}
              {!isLoading && missing.length === 0 && (
                <Badge variant="secondary" className="bg-green-100 text-green-700">
                  모두 등록되었습니다. 잠시 후 화면이 전환됩니다.
                </Badge>
              )}
            </div>
            {selected && (
              <div className="rounded-lg border bg-background p-4">
                <p className="mb-4 text-sm font-medium">{formatDate(selected)} 업무 보고</p>
                <WorkItemEditor date={selected} isToday={selected === toLocalYMD()} compact />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
